import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gspx } from '@rsos/capstone';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import AccessDenied from '@rsos/components/capstone/AccessDenied';
import BasemapMenu from '@rsos/components/capstone/BasemapMenu';
import { fetchInitialAlertAdr } from '@rsos/components/capstone/DataCards/adrDataSlice';
import Queue from '@rsos/components/capstone/Queue/Queue';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import GoogleMap from '@rsos/components/capstone/common/Map/Map';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import useMapProvider from '@rsos/hooks/useMapProvider';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import {
  fetchAlertConversationStatus,
  setChatSelectedAlertID,
} from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import {
  fetchAlert,
  removeAlert,
  resetIDWithNewAlert,
  setSelectedAlertID,
  viewAllAlerts,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import {
  removeCall,
  resetNumberWithNewLocation,
  setSelectedCallerID,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  setHoveredAlertID,
  setHoveredFrom,
  setHoveredID,
} from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { resetWhat3WordsLocation } from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import { getCallQueueMaxSize } from '@rsos/utils/metaTags';
import useCallTakerMode from '@rsos/utils/useCallTakerMode';
import useCallTakerModePerms from '@rsos/utils/useCallTakerModePerms';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePortalType from '@rsos/utils/usePortalType';
import usePrevious from '@rsos/utils/usePrevious';
import {
  ALERTS,
  EMERGENCY_CALLS,
} from '../../../../../libs/constants/incidentTypes';
import { deactivateSpatialLayerStatus } from '../../actions';
import { forceStaticLayerOn } from '../../actions/spatialLayers';
import { _911INFORM, _911_INFORM } from '../../constants/spatialLayers';
import useAlertsToastNotifications from '../../hooks/useAlertsToastNotifications';
import useHasLocationsToRender from '../../hooks/useHasLocationsToRender';
import useTrackUnreads from '../../hooks/useTrackUnreads';
import { persistor } from '../../store';
import {
  getMostRecentLoc,
  getMostRecentLocations,
} from '../../utils/getMostRecentLoc';
import { isAdminApproved } from '../../utils/isAdminApproved';
import AgencyShareChatNotifications from '../AgencyShareChat/AgencyShareChatNotifications';
import AlertPane from '../AlertPane';
import AppNotifications from '../AppNotifications';
import MapContainer from '../Common/MapContainer';
import { hide as hideRade } from '../JurisdictionView/RADECard/radeSlice';
import { ANI_ALI } from '../Location/constants/dataSources';
import { getDataSource, isSourceAniAli } from '../Location/helpers';
import MapLayersControlContainer from '../MapLayersControl/MapLayersControlContainer';
import CallPane from './CallPane';
import {
  FullWidthBox,
  EventDetailsContainer,
  MainContainer,
  SearchAndQueueContainer,
} from './JurisdictionView.styles';
import { Map as EsriMap } from './Map/Map';
import ConnectedUniversalSearchContainer from './UniversalSearch/ConnectedUniversalSearchContainer';
import W3wCallPane from './W3wCallPane';
import {
  isExtraInfoOrMeta,
  shouldRemoveCallFromQueue,
  shouldRemoveAlertFromQueue,
  isSpatialLayerActive,
  check911QueueExceedsMaxSize,
} from './helpers';
import {
  trackAlertMarkerClicks,
  trackCallMarkerClicks,
  trackCloseAlertPane,
  trackClickTab,
  trackHoverOn,
  trackHoverOnCall,
  trackHoverOnPin,
  trackClickOnCallFromMap,
  trackHoverOnCallWithTime,
  trackScreenResolutionChange,
} from './jurisdictionViewTrackings';

const JurisdictionView = ({
  hasAgent511Access,
  hasAlertsAccess,
  hasManageAccess,
  isJVEnabled,
  isShareChatEnabled,
}) => {
  const dispatch = useDispatch();
  const {
    numberWithNewLocation,
    selectedCallerID,
    viewNumLocations,
  } = useSelector(state => state.emergencyCalls);

  const what3words = useSelector(state => state.what3words.searched);
  const { IDWithNewAlert, selectedAlertID, removedAlerts } = useSelector(
    state => state.incidents,
  );
  const { layout } = useContext(LayoutContext);
  const clearHoveredTimer = useRef(null);
  const [activeQueueTab, setActiveQueueTab] = useState(
    selectedAlertID ? ALERTS : EMERGENCY_CALLS,
  );
  const [hoveredCall, setHoveredCall] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [removedID, setRemovedID] = useState(null);
  const [showPane, setShowPane] = useState(!!selectedAlertID);
  const [currentERA, setCurrentERA] = useState(null);
  const [timer, setTimer] = useState(0);
  const [callsHoveredList, setCallsHoveredList] = useState([]);
  const [showAccessDeniedOverlay, setShowAccessDeniedOverlay] = useState(false);
  const [showCallPane, setShowCallPane] = useState(!!selectedCallerID);
  const [showW3wCallPane, setShowW3wCallPane] = useState(false);
  const [mafError, setMafError] = useState(null);
  const [adrError, setADRError] = useState(null);
  const [triggerViewJurisdiction, setTriggerViewJurisdiction] = useState(false);
  const [clickedMarkerAnalytics, setClickedMarkerAnalytics] = useState(null);

  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));
  const { hoveredID, hoveredFrom } = useSelector(
    state => state.hoveredIncident,
  );
  const {
    currentRole,
    profile,
    isLoggedIn,
    application,
    currentOrg,
  } = useSelector(state => state.sinatra.user);
  const ctmPermission = useCallTakerModePerms();

  const userProfile = profile;
  const { currentPSAP } = useSelector(state => state.psaps);

  const {
    queryURI,
    irp_access_token,
    irpVersion,
    isIRPmode,
    isIRPresponsiveConditional,
  } = useIsIRP();
  const { isPremiumUser, portalType } = usePortalType();

  const { exact_polygon, expanded_polygon } = useSelector(
    state => state.geofences.currentGeofence,
  );

  const { allSpatialLayers } = useSelector(state => state.spatialLayers);
  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const hasCallStatusAccess = useSelector(state =>
    checkCallStatusAccessSelector({ psaps: state.psaps }),
  );

  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const theme = useSelector(state => state.sinatra.ui.theme);

  const hasLocation = !!(
    selectedCallerID &&
    filteredCalls[selectedCallerID] &&
    (filteredCalls[selectedCallerID].core.locations.length > 0 ||
      filteredCalls[selectedCallerID].core.secondaryLocations.length > 0)
  );

  const activeIncidentID =
    selectedAlertID && !removedAlerts.includes(selectedAlertID)
      ? selectedAlertID
      : selectedCallerID && hasLocation
      ? selectedCallerID
      : null;

  const inactiveIncidentID =
    !hasLocation && selectedCallerID
      ? selectedCallerID
      : selectedAlertID && !removedAlerts.includes(selectedAlertID)
      ? selectedAlertID
      : null;

  const userRole = currentRole.application;

  const isCentralStation = userRole === 'central_station';

  const incidents = {
    alerts,
    calls: filteredCalls,
  };

  const hasProductKey = useSelector(
    state =>
      state.sinatra.products.byName.Capstone ||
      state.sinatra.products.byName['Capstone-Pre-Production'],
  );

  const hasSecondaryLocation = !!useSelector(
    state =>
      !!state.emergencyCalls.selectedCallerID &&
      state.emergencyCalls.callQueue[selectedCallerID] &&
      state.emergencyCalls.callQueue[selectedCallerID].core &&
      state.emergencyCalls.callQueue[selectedCallerID].core
        .secondaryLocations &&
      state.emergencyCalls.callQueue[selectedCallerID].core.secondaryLocations
        .length > 0,
  );

  const isConnectedCar = isCallerIDFromOrg(
    'sxm',
    filteredCalls,
    selectedCallerID,
  );
  const hasW3w = !!what3words?.coordinates;

  const onViewJurisdiction = () => {
    // will recenter and reset zoom for Map each time
    setTriggerViewJurisdiction(prev => !prev);
  };

  useEffect(() => {
    if (!showW3wCallPane && hasW3w) {
      dispatch(resetWhat3WordsLocation());
    }
    if (!hasLocation && !selectedAlertID && !hasSecondaryLocation) {
      dispatch(viewAllCalls());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isJVEnabled) {
      const { jurisdictions } = currentPSAP;
      if (
        !hasProductKey ||
        (hasManageAccess && currentPSAP && !isAdminApproved(jurisdictions))
      ) {
        setShowAccessDeniedOverlay(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAccessDeniedOverlay]);

  /**
   * Handles clicking on a push notification for a call with 911inform data.
   * handleMarkerClick has the same handling for calls
   * @param {Object} options - configurable options required for specific handling
   */
  const handle911informNotificationClick = (options = {}) => {
    const callerId = options?.callerId;
    setActiveQueueTab(EMERGENCY_CALLS);
    dispatch(setSelectedCallerID(callerId));
    dispatch(setSelectedAlertID(null));
    dispatch(setChatSelectedAlertID(null));

    // Assume callerId has spatial layers data if there was a notification
    dispatch(forceStaticLayerOn(_911INFORM));
    if (hasW3w) {
      dispatch(resetWhat3WordsLocation());
    }

    gspx.trackCustomEvent('Data Map', {
      name: 'Click on 911inform notification',
      Category: 'Spatial Layer',
      'Launched date': new Date(),
    });
  };

  const handleCloseDataCard = useCallback(() => {
    trackCloseAlertPane(selectedAlertID, irpVersion);
    dispatch(viewAllAlerts());
  }, [selectedAlertID, irpVersion, dispatch]);

  const handleCloseCallPane = () => {
    dispatch(viewAllCalls());
    dispatch(hideRade());
    if (!hasW3w) {
      dispatch(resetWhat3WordsLocation());
      setShowW3wCallPane(false);
    }
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    dispatch(closeCard(AGENT_511_MAP_CARD));
  };

  useEffect(() => {
    setShowCallPane(!!selectedCallerID);
    if (!selectedCallerID) {
      dispatch(closeCard(AGENT_511_MAP_CARD));
      if (hasW3w) setShowW3wCallPane(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID]);

  useEffect(() => {
    if (selectedAlertID) {
      setShowPane(true);
      dispatch(hideRade());
      setShowW3wCallPane(false);
    }
    if (hasEsriMapAccess) {
      dispatch(resetWhat3WordsLocation());
    }
  }, [dispatch, selectedAlertID, hasEsriMapAccess]);

  const hasCTMPermission = useMemo(() => {
    return ctmPermission && !activeIncidentID;
  }, [ctmPermission, activeIncidentID]);

  useCallTakerMode(
    filteredCalls,
    hasCTMPermission,
    numberWithNewLocation,
    selectedCallerID,
    irpVersion,
  );

  /**
   * Generic handling for hover effect on queue item or marker
   * @param {String} incidentID - Alert or Caller ID
   * @param {String} origin - Where the hover orgin is - 'queue' or 'map'
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   */
  const handleHoverEffect = (incidentID, origin, incidentType, secondary) => {
    const QUEUE = 'queue';
    const MAP = 'map';
    if (incidentID && incidentType === EMERGENCY_CALLS) {
      let mostRecentLocation =
        origin === QUEUE
          ? getMostRecentLoc(filteredCalls[incidentID])
          : filteredCalls[incidentID].core.locations[0];

      if (secondary) {
        const { mostRecentSecondaryLocation } = getMostRecentLocations(
          filteredCalls[incidentID],
        );
        mostRecentLocation = mostRecentSecondaryLocation;
      }

      const isAniAli = isSourceAniAli(mostRecentLocation);

      const serviceType = isAniAli
        ? ANI_ALI
        : getDataSource(mostRecentLocation);

      const classOfServices = isAniAli
        ? mostRecentLocation?.class_of_service || null
        : null;

      const callStatus = getMostRecentCallStatus({
        callerID: incidentID,
        callQueue: filteredCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      if (origin === QUEUE) {
        const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);

        if (!callsHoveredList.includes(incidentID)) {
          trackHoverOn(
            isConnectedCar,
            irpVersion,
            payloadID,
            requestID,
            portalType,
            serviceType,
            classOfServices,
            callStatus,
          );
          setCallsHoveredList(callsHoveredList.concat(incidentID));
        }
        trackHoverOnCall(
          isConnectedCar,
          irpVersion,
          payloadID,
          requestID,
          portalType,
          serviceType,
          classOfServices,
          callStatus,
        );
      }

      if (origin === MAP) {
        trackHoverOnPin(irpVersion, serviceType, classOfServices, callStatus);
      }
    }
    dispatch(setHoveredFrom(origin));
    dispatch(setHoveredID(incidentID)); // Only used for Google maps hover handling

    // Esri maps calls/alerts hover handling for markers conflict due to their
    // `else` conditions for when a corresponding marker isn't hovered. This
    // provides more granularity
    if (incidentType === EMERGENCY_CALLS) {
      setHoveredCall(incidentID);
    } else if (incidentType === ALERTS) {
      dispatch(setHoveredAlertID(incidentID));
    }
  };

  /**
   * onClick handler for when clicking on a marker
   * @param {String} incidentID - selectedAlertID or selectedCallerID
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   * @param {Boolean} isAllView - Flag for if user is viewing all alerts/calls or not
   */
  const handleMarkerClick = (incidentID, incidentType, isAllView) => {
    // Note: `setActiveQueueTab` needs to be first in order to properly draw
    // markers when clicking on a marker type that isn't the activeQueueTab
    if (isAllView) {
      setClickedMarkerAnalytics({
        incidentID: incidentID,
        incidentType: incidentType,
      });
      if (incidentType === ALERTS) {
        setActiveQueueTab(ALERTS);
        // viewAllCalls must be dispatched first for MapCard handling
        dispatch(viewAllCalls());
        dispatch(
          fetchAlert({
            currentApplication: application,
            currentOrgName: currentOrg.name,
            alertID: incidentID,
            shouldSetID: true,
          }),
        ).then(() => {
          dispatch(setChatSelectedAlertID(incidentID));
          dispatch(
            fetchAlertConversationStatus({
              orgName: currentOrg.name,
              conversationID: `chat-portal-default-${
                incidentID.split('alert-')[1]
              }`,
            }),
          );
          dispatch(fetchInitialAlertAdr(incidentID));
        });
      } else {
        setActiveQueueTab(EMERGENCY_CALLS);
        trackClickOnCallFromMap(irpVersion);
        // viewAllAlerts must be dispatched first for MapCard handling
        dispatch(viewAllAlerts());
        dispatch(setSelectedCallerID(incidentID));
        dispatch(setChatSelectedAlertID(null));
        dispatch(setHoveredID(null));

        const has911informData = !!filteredCalls?.[incidentID]
          ?.additionalData?.[_911_INFORM];
        if (has911informData) {
          dispatch(forceStaticLayerOn(_911INFORM));
        }
      }
      if (hasW3w) {
        dispatch(resetWhat3WordsLocation());
      }

      dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    }
    setHoveredCall(null);
  };

  const handleMapReady = () => {
    setIsMapInitialized(true);
  };

  /**
   * Callback function to handle setting `numberWithNewLocation` and
   * `IDWithNewAlert` to null when a marker has been added based on those
   * values to prevent duplicate markers and losing their reference when
   * switching between views/queues
   * @param {String} incidentType - `EMERGENCY_CALLS` or `ALERTS`
   */
  const handleMarkerAdded = incidentType => {
    if (incidentType === EMERGENCY_CALLS) {
      dispatch(resetNumberWithNewLocation());
    } else if (incidentType === ALERTS) {
      dispatch(resetIDWithNewAlert());
    }
  };

  /**
   * Callback function for when a push notification is clicked on
   * @param {Object} options - configurable options required for specific handling
   * @param {String} options.handler - flag for which function to map the callback function to
   */
  const handlePushNotificationCallback = (
    options = {
      handler: '',
    },
  ) => {
    const handler = options.handler;
    switch (handler) {
      case _911INFORM: {
        handle911informNotificationClick(options);
        break;
      }
      default: {
        throw new Error('Include a handler value for the options');
      }
    }
  };

  const handleQueueTab = tab => {
    // Reset the views and data tab if the clicked tab is different than the
    // active one to prevent unexpected map behavior
    if (tab !== activeQueueTab) {
      setActiveQueueTab(tab);
    }
    // Hide spatial layers from Alerts view
    if (tab === ALERTS) {
      trackClickTab('Alerts', irpVersion);
    } else {
      trackClickTab('911', irpVersion);
    }
  };

  /**
   * Update state to remove an alert or call from the map. Cannot remove a call
   * that is being viewed. Do not update state if viewing another call, since
   * the removedID is used to remove a marker from the map and since the marker
   * doesn't exist when viewing another call, the state is never set back to
   * `null` when a call is removed. Currently only used for calls
   * @param {String} ID - Alert ID or caller ID
   */
  const handleRemoveID = useCallback(
    ID => {
      if (!activeIncidentID) {
        setRemovedID(ID);
      }
    },
    [activeIncidentID],
  );

  const handleCurrentERA = useCallback(payload => {
    setCurrentERA(payload);
  }, []);

  useMemo(() => {
    setCurrentERA(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID, selectedAlertID]);

  //Auto triggers alert tab for irpLogin with an alert ID
  useEffect(() => {
    if (queryURI && queryURI.includes('alert')) {
      setActiveQueueTab(ALERTS);
    }
  }, [queryURI]);

  /**
   * Removes a 911 call from Redux, used for checking inactive 911 calls.
   * @param {string} callerID - caller id in the 911 queue
   */
  const removeCallFromQueue = callerID => {
    // When viewing a call/alert, removedID will not get reset until
    // closing the detailed view, resulting in incorrect marker handling.
    if (!activeIncidentID) setRemovedID(callerID);
    dispatch(removeCall(callerID));
  };

  /**
   * Removes an alert from Redux, used for checking inactive alerts.
   * @param {string} alertID - alert id in the alerts queue
   */
  const removeAlertFromQueue = alertID => {
    // When viewing a call/alert, removedID will not get reset until
    // closing the detailed view, resulting in incorrect marker handling.
    if (!activeIncidentID) setRemovedID(alertID);
    dispatch(removeAlert(alertID));
  };

  /**
   * Deactivates a spatial layer status
   * @param {number} spatialLayedID - spatial layer ID
   */
  const handleDeactivateSpatialLayerStatus = spatialLayedID => {
    dispatch(deactivateSpatialLayerStatus(spatialLayedID));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(filteredCalls).map(callerID => {
        if (
          shouldRemoveCallFromQueue(
            filteredCalls[callerID],
            selectedCallerID,
            hasAgent511Access,
          )
        ) {
          removeCallFromQueue(callerID);
        }
      });

      Object.keys(alerts).map(alertID => {
        if (shouldRemoveAlertFromQueue(alerts[alertID], selectedAlertID)) {
          removeAlertFromQueue(alertID);
        }
      });

      allSpatialLayers.forEach(spatialLayer => {
        if (!isSpatialLayerActive(spatialLayer)) {
          handleDeactivateSpatialLayerStatus(spatialLayer.id);
        }
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents]);

  useEffect(() => {
    if (Object.keys(filteredCalls).length > getCallQueueMaxSize()) {
      check911QueueExceedsMaxSize(
        filteredCalls,
        selectedCallerID,
        removeCallFromQueue,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCalls]);

  const callLocations =
    selectedCallerID &&
    incidents.calls[selectedCallerID] &&
    incidents.calls[selectedCallerID].core
      ? incidents.calls[selectedCallerID].core.locations
      : [];
  const hasLocationsToRender = useHasLocationsToRender();
  const { height } = layout.body;

  // gainsight event tracking data
  useEffect(() => {
    if (!filteredCalls[hoveredID]) return;
    if (hoveredFrom && hoveredID) {
      const mostRecentLocation = getMostRecentLoc(filteredCalls[hoveredID]);
      const isAniAli = isSourceAniAli(mostRecentLocation);
      const serviceType = isAniAli
        ? ANI_ALI
        : getDataSource(mostRecentLocation);
      const classOfServices = isAniAli
        ? mostRecentLocation?.class_of_service || null
        : null;
      const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
      const callStatus = getMostRecentCallStatus({
        callerID: hoveredID,
        callQueue: filteredCalls,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      if (timer < 2) {
        clearHoveredTimer.current = setTimeout(
          () => setTimer(timer => timer + 1),
          1000,
        );
      }
      if (timer >= 1 && timer < 2) {
        trackHoverOnCallWithTime(
          1,
          hoveredFrom,
          isConnectedCar,
          irpVersion,
          payloadID,
          requestID,
          portalType,
          serviceType,
          classOfServices,
          callStatus,
        );
      }
      if (timer >= 2) {
        trackHoverOnCallWithTime(
          2,
          hoveredFrom,
          isConnectedCar,
          irpVersion,
          payloadID,
          requestID,
          portalType,
          serviceType,
          classOfServices,
          callStatus,
        );
      }
    }
    if (!hoveredID) setTimer(0);
    return () => {
      clearTimeout(clearHoveredTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredFrom, hoveredID, timer]);

  const handleQueueItemClicked = id => {
    if (id !== selectedCallerID) {
      dispatch(resetWhat3WordsLocation());
      if (hasEsriMapAccess) {
        setShowW3wCallPane(false);
      }
    }
    dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));

    setShowCallPane(true);
  };

  // Google markers don't currently update handle click
  // If google markers' rendering changes can move into handleMarkerClick again
  useEffect(() => {
    if (clickedMarkerAnalytics?.incidentType) {
      if (clickedMarkerAnalytics.incidentType === ALERTS) {
        trackAlertMarkerClicks(
          activeQueueTab,
          irpVersion,
          clickedMarkerAnalytics.incidentID,
        );
      } else {
        trackCallMarkerClicks(activeQueueTab, irpVersion);
      }
      setClickedMarkerAnalytics(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedMarkerAnalytics]);

  const windowWidth = layout.window.width;
  const prevWindowWidth = usePrevious(windowWidth);

  useEffect(() => {
    if (windowWidth !== prevWindowWidth) {
      trackScreenResolutionChange(irpVersion, prevWindowWidth, windowWidth);
    }
  }, [irpVersion, prevWindowWidth, windowWidth]);
  const hideQueueConditional =
    !isIRPresponsiveConditional &&
    windowWidth <= BP_LAPTOP &&
    ((selectedCallerID && hasLocationsToRender) || selectedAlertID);

  let currentLocation =
    hasLocationsToRender && getMostRecentLoc(filteredCalls[selectedCallerID]);
  if (currentLocation) {
    const { latitude, longitude } = currentLocation;
    currentLocation = { latitude, longitude };
  }
  let shapes;
  const jurisdictions = currentPSAP?.jurisdictions;
  if (Array.isArray(jurisdictions) && jurisdictions.length > 0) {
    shapes = jurisdictions[0].shapes;
  }

  /**
   * Manage All Alert Toast Notifications
   **/
  useAlertsToastNotifications();

  // Track unread alerts and calls via TalkJS when no alert or call is selected,
  // or when a call is selected but not shared or received via agency share/chat.
  useTrackUnreads();

  // If the filtered calls doesn't contain the selectedCallerID on mount, assume
  // that the call was filtered out due to ANI/ALI permissions/capability being
  // turned off. Reset selectedCallerID
  useEffect(() => {
    if (selectedCallerID && !filteredCalls[selectedCallerID]) {
      dispatch(setSelectedCallerID(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set initial map provider.
  useMapProvider();

  return (
    <>
      <AppNotifications onClickNotification={handlePushNotificationCallback} />
      {isShareChatEnabled && (
        <AgencyShareChatNotifications setShowCallPane={setShowCallPane} />
      )}
      {showAccessDeniedOverlay && <AccessDenied persistor={persistor} />}
      <MainContainer isIRP={isIRPresponsiveConditional}>
        <SearchAndQueueContainer isIRP={isIRPresponsiveConditional}>
          <ConnectedUniversalSearchContainer
            filteredCalls={filteredCalls}
            hasManageAccess={hasManageAccess}
            isInternational={isInternational}
            setShowW3wCallPane={setShowW3wCallPane}
            mafError={mafError}
            setMafError={setMafError}
            adrError={adrError}
            setADRError={setADRError}
            setActiveQueueTab={setActiveQueueTab}
          />
          {!hideQueueConditional && (
            <Queue
              activeIncidentID={activeIncidentID}
              inactiveIncidentID={inactiveIncidentID}
              activeQueueTab={activeQueueTab}
              filteredCalls={filteredCalls}
              hoveredCall={hoveredCall}
              hasAgent511Access={hasAgent511Access}
              hasAlertsAccess={hasAlertsAccess}
              isEmergencyCallsEnabled={true}
              isInternational={isInternational}
              isJVEnabled={isJVEnabled}
              irpAlertsID={queryURI}
              selectedAlertID={selectedAlertID}
              selectedCallerID={selectedCallerID}
              setActiveQueueTab={setActiveQueueTab}
              setShowPane={setShowPane}
              onQueueHover={handleHoverEffect}
              onQueueTabClick={handleQueueTab}
              onRemoveID={handleRemoveID}
              onQueueItemClicked={handleQueueItemClicked}
              setRemovedAlertID={setRemovedID}
              hasEsriMapAccess={hasEsriMapAccess}
              onViewJurisdiction={onViewJurisdiction}
            />
          )}
        </SearchAndQueueContainer>

        <FullWidthBox isViewingAlerts={!!selectedAlertID}>
          <>
            <EventDetailsContainer
              isIRP={isIRPresponsiveConditional}
              selectedItem={selectedAlertID || selectedCallerID}
              isAllCallsSelected={!selectedCallerID}
            >
              {selectedAlertID && showPane && (
                <AlertPane
                  onClose={handleCloseDataCard}
                  onRemoveID={handleRemoveID}
                />
              )}
              {filteredCalls[selectedCallerID] &&
                ((hasLocation && hasLocationsToRender) ||
                  hasSecondaryLocation) &&
                (isMapInitialized || irp_access_token) &&
                showCallPane && (
                  <CallPane
                    height={height}
                    callerID={selectedCallerID}
                    locations={callLocations}
                    isReverseGeocoding={false}
                    isInternational={isInternational}
                    selectedCall={selectedCallerID}
                    onActionableButton={handleCurrentERA}
                    onClose={handleCloseCallPane}
                    hasW3w={hasW3w}
                  />
                )}
              {!selectedAlertID &&
                (!selectedCallerID ||
                  (selectedCallerID && !hasLocationsToRender)) &&
                hasW3w &&
                showW3wCallPane && (
                  <W3wCallPane
                    hasLocationsToRender={hasLocationsToRender}
                    hasW3w={hasW3w}
                  />
                )}
              <MapContainer
                hasLocationsToRender={hasLocationsToRender}
                hasW3wToRender={
                  hasW3w && showW3wCallPane && !inactiveIncidentID
                }
                selectedAlertID={selectedAlertID}
                isIRP={isIRPresponsiveConditional}
                selectedItem={selectedAlertID || selectedCallerID}
              >
                <BasemapMenu />
                {isMapInitialized && <MapLayersControlContainer />}
                {(!irp_access_token || isIRPmode) && isLoggedIn ? (
                  <>
                    {mapProvider &&
                      (mapProvider === ESRI ? (
                        <EsriMap
                          activeQueueTab={activeQueueTab}
                          currentERA={currentERA}
                          hoveredCall={hoveredCall}
                          onMapReady={handleMapReady}
                          onMarkerAdd={handleMarkerAdded}
                          onMarkerClick={handleMarkerClick}
                          onMarkerHover={handleHoverEffect}
                          onRemoveID={handleRemoveID}
                          removedID={removedID}
                          themeName={theme}
                          setMafError={setMafError}
                          setADRError={setADRError}
                          triggerViewJurisdiction={triggerViewJurisdiction}
                        />
                      ) : (
                        <GoogleMap
                          activeQueueTab={activeQueueTab}
                          activeIncidentID={activeIncidentID}
                          boundingBox={shapes}
                          currentERA={currentERA}
                          inactiveIncidentID={inactiveIncidentID}
                          hasW3w={hasW3w}
                          IDWithNewAlert={IDWithNewAlert}
                          incidents={incidents}
                          incidentType={
                            selectedAlertID
                              ? ALERTS
                              : selectedCallerID
                              ? EMERGENCY_CALLS
                              : activeQueueTab
                          }
                          isAllView={!activeIncidentID}
                          isInternational={isInternational}
                          isCentralStation={isCentralStation}
                          mapView={'Jurisdiction'}
                          numberWithNewLocation={numberWithNewLocation}
                          onMapReady={handleMapReady}
                          onMarkerAdd={handleMarkerAdded}
                          onMarkerClick={handleMarkerClick}
                          onMarkerHover={handleHoverEffect}
                          onRemoveID={handleRemoveID}
                          polygon={exact_polygon}
                          expandedPolygon={expanded_polygon}
                          removedID={removedID}
                          setMafError={setMafError}
                          setADRError={setADRError}
                          themeName={theme}
                          userProfile={userProfile}
                          viewNumLocations={viewNumLocations}
                          triggerViewJurisdiction={triggerViewJurisdiction}
                          filteredCalls={filteredCalls}
                          hasCallStatus={hasCallStatusAccess && hasAniAliAccess}
                        />
                      ))}
                  </>
                ) : null}
              </MapContainer>
            </EventDetailsContainer>
          </>
        </FullWidthBox>
      </MainContainer>
    </>
  );
};

export default React.memo(JurisdictionView);
